import { useEffect, useMemo, useState } from 'react';
import { freshNews, hotTopics, specials } from '@/mocks/chaoyang';

const navs = [
  { id: 'today', name: '今日热瓜', icon: 'ri-fire-line' },
  { id: 'fresh', name: '今日新瓜', icon: 'ri-flashlight-line' },
  { id: 'scene', name: '现场速递', icon: 'ri-camera-lens-line' },
  { id: 'special', name: '必看专题', icon: 'ri-sparkling-2-line' },
  { id: 'about', name: '瓜友口碑', icon: 'ri-chat-smile-3-line' },
  { id: 'faq', name: '常见问题', icon: 'ri-question-line' },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('today');
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      let current = 'today';
      navs.forEach((n) => {
        const el = document.getElementById(n.id);
        if (el && el.getBoundingClientRect().top <= 160) current = n.id;
      });
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goAnchor = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    const list: { title: string; type: string; anchor: string }[] = [];
    freshNews.forEach((n) => {
      if ((n.title + n.category + n.area).toLowerCase().includes(q)) list.push({ title: n.title, type: '今日新瓜', anchor: 'fresh' });
    });
    hotTopics.forEach((t) => {
      if (t.title.includes(q)) list.push({ title: t.title, type: '热榜', anchor: 'today' });
    });
    specials.forEach((s) => {
      if ((s.title + s.category).toLowerCase().includes(q)) list.push({ title: s.title, type: '专题', anchor: 'special' });
    });
    return list.slice(0, 6);
  }, [query]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#fffdf8]/90 shadow-[0_1px_0_0_rgba(232,88,43,0.08)] backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="chigua-container flex h-16 items-center justify-between gap-4 md:h-[72px]">
        <button type="button" onClick={() => goAnchor('top')} className="flex items-center gap-2.5 cursor-pointer" aria-label="朝阳吃瓜首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-red-600 text-lg font-black text-white shadow-[0_6px_18px_rgba(232,88,43,0.35)]">
            瓜
          </span>
          <span className="flex flex-col items-start leading-none">
            <span className="text-xl font-black tracking-tight text-stone-900">朝阳吃瓜</span>
            <span className="mt-1 text-[11px] font-medium text-orange-700">瓜要趁热吃 · 料要现场挖</span>
          </span>
        </button>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navs.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => goAnchor(n.id)}
              className={`relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                active === n.id ? 'bg-orange-500/10 text-orange-700' : 'text-stone-700 hover:bg-black/5 hover:text-orange-700'
              }`}
            >
              {n.name}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-stone-700 transition-colors hover:border-orange-500 hover:text-orange-600 cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-stone-800 lg:hidden cursor-pointer"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-orange-500/10 bg-[#fffdf8] px-4 py-3 md:px-6">
          <div className="chigua-container flex items-center gap-2 rounded-full bg-black/5 px-4">
            <i className="ri-search-line text-lg text-orange-600"></i>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜瓜：输入关键词，如「三里屯」「开业」「音乐会」"
              className="h-11 w-full bg-transparent text-sm text-stone-800 outline-none placeholder:text-stone-400"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-stone-400 hover:text-stone-600 cursor-pointer" aria-label="清空">
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
          </div>
          {query.trim() && (
            <div className="chigua-container mt-3 max-h-64 overflow-y-auto">
              {results.length ? (
                <ul className="divide-y divide-black/5">
                  {results.map((r, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => {
                          goAnchor(r.anchor);
                          setSearchOpen(false);
                        }}
                        className="flex w-full items-start gap-3 px-1 py-3 text-left cursor-pointer hover:bg-orange-500/5"
                      >
                        <span className="mt-0.5 whitespace-nowrap rounded-md bg-orange-500/10 px-2 py-0.5 text-[11px] font-medium text-orange-700">{r.type}</span>
                        <span className="line-clamp-1 text-sm text-stone-800">{r.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-1 py-4 text-sm text-stone-500">没有搜到相关的瓜，换个关键词试试。</p>
              )}
            </div>
          )}
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-orange-500/10 bg-[#fffdf8] lg:hidden">
          <nav className="chigua-container grid grid-cols-3 gap-2 py-4" aria-label="移动端菜单">
            {navs.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => goAnchor(n.id)}
                className={`flex flex-col items-center gap-1.5 rounded-xl py-3 text-sm font-medium cursor-pointer ${
                  active === n.id ? 'bg-orange-500/10 text-orange-700' : 'bg-black/5 text-stone-700'
                }`}
              >
                <i className={`${n.icon} text-lg`}></i>
                {n.name}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}